import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import EventFinished from "src/components/EventFinished";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import { useWindowWidth } from "src/utils/windowUtils";
import { isAppWebView } from "src/utils/isAppWebView";

const isAppWebViewFlag = isAppWebView();

const TierMatch2026 = () => {
  const { t, i18n } = useTranslation();
  const windowWidth = useWindowWidth();
  const [cardsReady, setCardsReady] = useState(false);
  const [daysReady, setDaysReady] = useState(false);
  const heroBg = useResponsiveImageUrl(
    "promotions/tier-match/september_2026/tier-match-hero-image-2400x800px.jpg",
  );
  const daysBg = useResponsiveImageUrl(
    windowWidth < 992
      ? "promotions/tier-match/september_2026/tier-match-90-days-bg-mobile.png"
      : "promotions/tier-match/september_2026/tier-match-90-days-bg-desktop.png",
  );

  const titleLines = t("tierMatch2026.hero.title").split("\n");

  return (
    <TierMatch2026Div>
      <EventFinished scheduleKeyName="tierMatch2026" eventType="promotion" />

      <HeroSection bgImage={heroBg} $isAppWebView={isAppWebViewFlag}>
        <container-div>
          <HeroRow>
            <div className="text-col">
              <Eyebrow>{t("tierMatch2026.hero.eyebrow")}</Eyebrow>
              <h1>
                {titleLines.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </h1>
              <p>{t("tierMatch2026.hero.subtitle")}</p>
            </div>
            <CardFrame>
              <ResponsiveImage
                src="promotions/tier-match/september_2026/tier-match-Cards.png"
                alt={t("tierMatch2026.hero.cardsAlt")}
                onLoad={(event) =>
                  setCardsReady(event.currentTarget.naturalWidth > 0)
                }
                onError={() => setCardsReady(false)}
                style={{ visibility: cardsReady ? "visible" : "hidden" }}
              />
            </CardFrame>
          </HeroRow>
        </container-div>
      </HeroSection>

      <GetStartedSection>
        <container-div>
          <h2>{t("tierMatch2026.content.getStartedTitle")}</h2>
          <Rule />
          <p>{t("tierMatch2026.content.getStartedParagraph1")}</p>
          <p>{t("tierMatch2026.content.getStartedParagraph2")}</p>
        </container-div>
      </GetStartedSection>

      <KeepSection bgImage={daysBg}>
        <KeepCopy>
          <h2>{t("tierMatch2026.content.keepStatusTitle")}</h2>
          <Rule $align="left" />
          <DaysImage
            src={`promotions/tier-match/september_2026/tier-match-90-days-${i18n.language}.png`}
            alt={t("tierMatch2026.content.daysAlt")}
            onLoad={(event) =>
              setDaysReady(event.currentTarget.naturalWidth > 0)
            }
            onError={() => setDaysReady(false)}
            style={{ display: daysReady ? "block" : "none" }}
          />
          {!daysReady && (
            <DaysLockup aria-label={t("tierMatch2026.content.daysAlt")}>
              <span>90</span>
              <span>{t("tierMatch2026.content.daysLabel")}</span>
            </DaysLockup>
          )}
        </KeepCopy>
        <KeepPhoto bgImage={daysBg}>
          <p>{t("tierMatch2026.content.keepStatusDescription")}</p>
        </KeepPhoto>
      </KeepSection>

      <DetailsSection>
        <container-div>
          <HostLine>{t("tierMatch2026.content.hostRoom")}</HostLine>
          <Terms>{t("tierMatch2026.content.terms")}</Terms>
        </container-div>
      </DetailsSection>
    </TierMatch2026Div>
  );
};

const TierMatch2026Div = styled.div`
  width: 100%;
  height: auto;
  background: var(--black);
  color: var(--white);
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background-color: #000;
  background-image: url(${(props) => props.bgImage}),
    repeating-linear-gradient(
      -32deg,
      #050505 0 10px,
      #141414 10px 11px
    );
  background-size: cover, auto;
  background-position: center;
  color: var(--white);
  padding: 170px 0 4.5rem;

  .text-col {
    flex: 1;
    min-width: 0;

    h1 {
      font-weight: 800;
      font-size: clamp(2.2rem, calc((min(100vw, 1200px) - 640px) / 9), 3.75rem);
      line-height: 1.02;
      margin: 0.35rem 0 1.1rem;
      letter-spacing: -0.02em;

      span {
        display: block;
      }
    }

    p {
      font-size: 1.2rem;
      line-height: 1.45;
      margin: 0;
      max-width: 520px;
    }
  }

  @media (max-width: 991px) {
    margin-top: ${(props) => (props.$isAppWebView ? "0" : "80px")};
    padding: 2.5rem 0 3rem;
    text-align: center;

    .text-col {
      h1 {
        font-size: clamp(2rem, 8vw, 3.15rem);
      }

      p {
        margin: 0 auto;
      }
    }
  }
`;

const HeroRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 991px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Eyebrow = styled.p`
  color: #e70a02;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0;
`;

const CardFrame = styled.div`
  flex: 0 0 533px;
  width: 533px;

  img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
  }

  @media (max-width: 991px) {
    flex-basis: auto;
    width: min(100%, 280px);
    margin: 0.5rem auto 0;
  }
`;

const GetStartedSection = styled.section`
  background: #fff;
  color: #111;
  text-align: center;
  padding: 3.5rem 0 3.25rem;

  h2 {
    font-weight: 800;
    font-size: 2.5rem;
    margin: 0;
    color: #111;
  }

  p {
    max-width: 760px;
    margin: 0.85rem auto 0;
    font-size: 1.05rem;
    line-height: 1.65;
    color: #222;
  }

  @media (max-width: 768px) {
    padding: 2.5rem 0;

    h2 {
      font-size: 1.85rem;
    }
  }
`;

const Rule = styled.span`
  display: block;
  width: 140px;
  height: 3px;
  background: #e70a02;
  margin: 0.85rem auto 1.35rem;
  margin-left: ${(props) => (props.$align === "left" ? "0" : "auto")};
  margin-right: ${(props) => (props.$align === "left" ? "0" : "auto")};
`;

const KeepSection = styled.section`
  display: grid;
  grid-template-columns: minmax(300px, 46%) 1fr;
  background-color: #000;
  background-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.25) 0%,
      rgba(0, 0, 0, 0.35) 40%,
      rgba(0, 0, 0, 0) 80%
    ),
    url(${(props) => props.bgImage});
  background-size: cover, cover;
  background-position: center, right bottom;
  background-repeat: no-repeat;
  color: var(--white);

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    background-image: none;
  }
`;

const KeepCopy = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 3.5rem 2.5rem 3.5rem max(1.5rem, calc((100vw - 1200px) / 2));

  h2 {
    font-weight: 800;
    font-size: 2rem;
    margin: 0;
    color: var(--white);
  }

  @media (max-width: 991px) {
    padding: 2.5rem 1.5rem 0.75rem;
    align-items: center;
    text-align: center;

    ${Rule} {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

const DaysLockup = styled.p`
  margin: 0.25rem 0 0;
  color: var(--white);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.03em;
  line-height: 0.82;

  span {
    display: block;
  }

  span:first-child {
    font-size: clamp(5.5rem, 10vw, 8.5rem);
  }

  span:last-child {
    font-size: clamp(3.2rem, 6vw, 5rem);
    letter-spacing: 0.01em;
  }
`;

const DaysImage = styled(ResponsiveImage)`
  width: min(100%, 340px);
  margin-top: 0.5rem;

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`;

const KeepPhoto = styled.div`
  display: flex;
  align-items: center;
  min-height: 380px;
  padding: 3rem max(2rem, calc((100vw - 1200px) / 2)) 3rem 2.5rem;

  p {
    position: relative;
    margin: 0;
    max-width: 460px;
    padding: 1.75rem 2.5rem;
    color: var(--white);
    font-size: 1.15rem;
    line-height: 1.6;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0.35em;
      bottom: 0.3em;
      width: 0.5px;
      background: rgba(255, 255, 255, 0.9);
    }
  }

  @media (max-width: 991px) {
    align-items: flex-start;
    min-height: 280px;
    padding: 0.9rem 1.5rem 3.25rem;
    background-color: #000;
    font-size: 18px;
    background-image: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.45) 0%,
        rgba(0, 0, 0, 0.35) 72%,
        rgba(0, 0, 0, 0) 100%
      ),
      url(${(props) => props.bgImage});
    background-size: cover, cover;
    background-position: center, center bottom;
    background-repeat: no-repeat;

    p {
      max-width: 640px;
      padding: 0.25rem 0 0.35rem 1.75rem;
    }
  }
`;

const DetailsSection = styled.section`
  background: #fff;
  color: #111;
  text-align: center;
  padding: 2.75rem 0 3.25rem;
`;

const HostLine = styled.p`
  font-weight: 700;
  font-size: 1.15rem;
  margin: 0 auto 1rem;
  max-width: 720px;
  color: #111;
`;

const Terms = styled.p`
  font-style: italic;
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 0 auto;
  max-width: 680px;
  color: #333;
`;

export default TierMatch2026;
